import type { ReactNode } from 'react';

import { useAuth } from '@/hooks/useAuth';
import type { AlertDialogAction } from '@components/AlertDialog/AlertDialog';
import { AlertDialog } from '@components/AlertDialog/AlertDialog';
import { Button } from '@components/Button/Button';
import { ScreenHeader } from '@components/ScreenHeader';
import { TabScreenContainer } from '@components/TabScreenContainer';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { ScrollView, Text, YStack } from 'tamagui';
import { LanguageSelect } from './components/LanguageSelect';
import { PersonalInformation } from './components/PersonalInformation';

export default function ProfileScreen(): ReactNode {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const { logout } = useAuth();

  const handleLogout: () => void = () => {
    void logout();
  };

  const logoutActions: AlertDialogAction[] = [
    {
      label: t('buttons.cancel'),
      variant: 'outlined',
      actionType: 'cancel',
    },
    {
      label: t('profile.logout'),
      onPress: handleLogout,
      variant: 'destructive',
      actionType: 'action',
    },
  ];

  return (
    <TabScreenContainer>
      <ScreenHeader
        title={t('navigation.profile')}
        actions={
          !isSmallScreen && (
            <AlertDialog
              title={t('profile.logoutDialog.title')}
              description={t('profile.logoutDialog.description')}
              actions={logoutActions}
            >
              <Button
                variant="outlined-destructive"
                title={t('profile.logout')}
              />
            </AlertDialog>
          )
        }
      />

      <ScrollView>
        <YStack alignItems="center" gap="$true">
          <YStack
            gap="$6"
            width="100%"
            maxWidth={isSmallScreen ? '100%' : '$compactPageWidth'}
          >
            <YStack gap="$true">
              <Text fontFamily="$bold">
                {t('profile.personalInformation.title')}
              </Text>
              <PersonalInformation />
            </YStack>

            <YStack gap="$2">
              <Text fontFamily="$bold">{t('profile.settings')}</Text>
              <Text>{t('profile.language')}</Text>
              <LanguageSelect />
            </YStack>

            {isSmallScreen && (
              <AlertDialog
                title={t('profile.logoutDialog.title')}
                description={t('profile.logoutDialog.description')}
                actions={logoutActions}
              >
                <Button
                  variant="outlined-destructive"
                  title={t('profile.logout')}
                  width="100%"
                />
              </AlertDialog>
            )}
          </YStack>
        </YStack>
      </ScrollView>
    </TabScreenContainer>
  );
}
